import { useState } from "react";
import { useToast } from "@hightouch/htui";
import type { WebPageContent } from "src/pages/ad-studio/components/web-page-preview";
import { buildWebPageZip } from "src/pages/ad-studio/files/web-page-zip";
import { downloadBlob, sanitizeFilename } from "src/utils/download";
import { useRegionConfig } from "src/utils/use-region-config";

/** Downloads a web page as a zip of `index.html` and its images. */
export function useDownloadWebPage() {
  const { apiEndpoint } = useRegionConfig();
  const { toast } = useToast();
  const [isDownloading, setIsDownloading] = useState(false);

  const download = async (name: string, { html, assets }: WebPageContent) => {
    setIsDownloading(true);
    try {
      const { blob, missing } = await buildWebPageZip({
        html,
        assets,
        apiBase: apiEndpoint,
      });
      downloadBlob(blob, `${sanitizeFilename(name) || "Untitled"}.zip`);
      if (missing.length > 0) {
        toast({
          id: "file-download",
          title: "Some images couldn't be downloaded",
          message: `Missing: ${missing.join(", ")}`,
          variant: "warning",
        });
      }
    } catch (error) {
      toast({
        id: "file-download",
        title: "Couldn't download the page",
        message: error instanceof Error ? error.message : undefined,
        variant: "error",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { download, isDownloading };
}
